
import { useEffect, useState } from 'react';

interface ScanningAnimationProps {
  username: string;
  onComplete: () => void;
}

export default function ScanningAnimation({ username, onComplete }: ScanningAnimationProps) {
  const [progress, setProgress] = useState(0);
  const [currentStatus, setCurrentStatus] = useState(0);
  const [stats, setStats] = useState({
    friends: 0,
    views: 0,
    likes: 0,
  });

  const statuses = [
    'Подключение к серверам Telegram...',
    'Анализ активности профиля...',
    'Обнаружено 5 активных пользователей',
    'Проверка частоты просмотров...',
    'Анализ настроений завершен',
    'Определение тайного фаната...',
    'Сканирование завершено!'
  ];

  useEffect(() => {
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          setTimeout(() => onComplete(), 500);
          return 100;
        }
        return prev + 2;
      });
    }, 100);

    const statusInterval = setInterval(() => {
      setCurrentStatus(prev => {
        if (prev < statuses.length - 1) {
          return prev + 1;
        }
        return prev;
      });
    }, 1000);

    const statsInterval = setInterval(() => {
      setStats(prev => ({
        friends: Math.min(prev.friends + Math.floor(Math.random() * 3) + 1, 47),
        views: Math.min(prev.views + Math.floor(Math.random() * 5) + 2, 156),
        likes: Math.min(prev.likes + Math.floor(Math.random() * 2) + 1, 89),
      }));
    }, 200);

    return () => {
      clearInterval(progressInterval);
      clearInterval(statusInterval);
      clearInterval(statsInterval);
    };
  }, [onComplete]);

  return (
    <div className="flex flex-col min-h-screen bg-[#0f0f0f]">
      {/* Telegram Header */}
      <div className="w-full bg-[#212121] px-4 py-3 flex items-center gap-3">
        <div className="w-10 h-10 bg-[#2AABEE] rounded-full flex items-center justify-center">
          <i className="ri-radar-line text-white text-xl animate-spin"></i>
        </div>
        <div>
          <h1 className="text-white font-semibold text-base">Сканирование</h1>
          <p className="text-[#8E8E93] text-xs">@{username}</p>
        </div>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center p-6">
        {/* Avatar with scanning animation */}
        <div className="relative mb-8">
          {/* Scanning rings */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="absolute w-32 h-32 border-4 border-[#2AABEE] rounded-full animate-ping opacity-20"></div>
            <div className="absolute w-40 h-40 border-4 border-[#64D2FF] rounded-full animate-ping opacity-10" style={{ animationDelay: '0.3s' }}></div>
            <div className="absolute w-48 h-48 border-4 border-[#2AABEE] rounded-full animate-ping opacity-5" style={{ animationDelay: '0.6s' }}></div>
          </div>

          {/* Main avatar */}
          <div className="relative w-28 h-28 bg-[#2AABEE] rounded-full flex items-center justify-center z-10 shadow-lg">
            <i className="ri-user-line text-white text-5xl"></i>
          </div>

          {/* Scanning line */}
          <div className="absolute inset-0 flex items-center justify-center overflow-hidden rounded-full">
            <div className="absolute w-full h-1 bg-gradient-to-r from-transparent via-[#64D2FF] to-transparent animate-scan"></div>
          </div>

          {/* Floating hearts */}
          <div className="absolute -top-2 -right-2 w-8 h-8 bg-[#FF3B30] rounded-full flex items-center justify-center animate-bounce">
            <i className="ri-heart-fill text-white text-sm"></i>
          </div>
          <div className="absolute -bottom-2 -left-2 w-6 h-6 bg-[#34C759] rounded-full flex items-center justify-center animate-pulse">
            <i className="ri-eye-fill text-white text-xs"></i>
          </div>
        </div>

        {/* Status text */}
        <div className="text-center mb-8">
          <h2 className="text-xl font-semibold text-white mb-2">
            {statuses[currentStatus]}
          </h2>
          <p className="text-sm text-[#8E8E93]">
            Пожалуйста, подождите...
          </p>
        </div>

        {/* Progress bar */}
        <div className="w-full max-w-sm mb-8">
          <div className="bg-[#1C1C1E] rounded-full h-2 overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-[#2AABEE] to-[#64D2FF] transition-all duration-300 ease-out relative"
              style={{ width: `${progress}%` }}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer"></div>
            </div>
          </div>
          <div className="flex justify-between items-center mt-2">
            <span className="text-xs text-[#8E8E93]">Прогресс</span>
            <span className="text-xs text-[#2AABEE] font-medium">{progress}%</span>
          </div>
        </div>

        {/* Stats cards */}
        <div className="w-full max-w-sm space-y-3">
          <div className="bg-[#1C1C1E] rounded-xl p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#2AABEE]/20 rounded-full flex items-center justify-center">
                <i className="ri-user-line text-[#2AABEE] text-lg"></i>
              </div>
              <span className="text-white text-sm">Проверено друзей</span>
            </div>
            <span className="text-[#2AABEE] font-semibold text-lg">{stats.friends}</span>
          </div>

          <div className="bg-[#1C1C1E] rounded-xl p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#64D2FF]/20 rounded-full flex items-center justify-center">
                <i className="ri-eye-line text-[#64D2FF] text-lg"></i>
              </div>
              <span className="text-white text-sm">Просмотров профиля</span>
            </div>
            <span className="text-[#64D2FF] font-semibold text-lg">{stats.views}</span>
          </div>

          <div className="bg-[#1C1C1E] rounded-xl p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#FF3B30]/20 rounded-full flex items-center justify-center">
                <i className="ri-heart-line text-[#FF3B30] text-lg"></i>
              </div>
              <span className="text-white text-sm">Активных реакций</span>
            </div>
            <span className="text-[#FF3B30] font-semibold text-lg">{stats.likes}</span>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes scan {
          0% { transform: translateY(-100%) rotate(0deg); }
          100% { transform: translateY(100%) rotate(360deg); }
        }
        .animate-scan {
          animation: scan 2s linear infinite;
        }
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .animate-shimmer {
          animation: shimmer 1.5s infinite;
        }
      `}</style>
    </div>
  );
}
